import { useMemo, useState } from "react";
import { Plus } from "@repo/ui";
import { useBrandsQuery } from "~/apis/use-brands-query";
import { useCustomersQuery } from "~/apis/use-customers-query";
import { useTasks } from "~/apis/use-tasks";
import BigButton from "~/components/ui/big-button";
import { PageContentLayout } from "~/components/ui/page-content-layout";
import { PageHeader } from "~/components/ui/page-header";
import { PageLayout } from "~/components/ui/page-layout";
import { useConfigurationSync } from "~/hooks/use-sync";
import type { Brand as BrandDto, Task } from "~/types/api";
import { Brand } from "./brand/brand";
import { BrandCreation } from "./brand-creation/brand-creation";
import { BrandCreationContextProvider } from "./contexts/brand-creation-context-provider";

export type BrandSettingsDTO = BrandDto & {
  customerName: string;
};

export const BrandSettingsPage = () => {
  useConfigurationSync("Brand");
  const { data: brands, isLoading, isPending } = useBrandsQuery();
  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isPending: isPendingCustomer,
  } = useCustomersQuery();
  const { tasks, isLoading: isLoadingTasks, isLoading: isPendingTasks } = useTasks();
  const [isCreating, setIsCreating] = useState(false);
  const isDataLoading = isLoading || isLoadingCustomer || isLoadingTasks;
  const isDataPending = isPending || isPendingCustomer || isPendingTasks;

  const brandsWithCustomerName = useMemo(() => {
    if (isDataLoading || isDataPending || !brands || !customer) return [];

    const customerById = new Map(customer.map((customerItem) => [customerItem.id, customerItem]));

    return brands.map((brand) => {
      const customerObj = customerById.get(brand.customerId);
      return {
        ...brand,
        customerName: customerObj?.name || "Unknown",
      };
    });
  }, [isDataLoading, isDataPending, brands, customer]);

  return (
    <PageLayout>
      <PageHeader
        title="Brand Settings"
        controls={
          <BigButton onClick={() => setIsCreating(true)} icon={<Plus />} iconPosition="right">
            <span className="max-lg:hidden">Create</span>
          </BigButton>
        }
      />
      <PageContentLayout>
        <div className="card-border flex flex-col gap-3 bg-white p-6">
          {isCreating && (
            <BrandCreationContextProvider>
              <BrandCreation onClose={() => setIsCreating(false)} />
            </BrandCreationContextProvider>
          )}
          {brandsWithCustomerName.map((brand) => (
            <Brand
              key={brand.id}
              hasActiveTasks={tasks.some((task: Task) => task.brandId === brand.id)}
              brand={brand}
            />
          ))}
        </div>
      </PageContentLayout>
    </PageLayout>
  );
};
